import React, { FC, useState } from 'react';
import { TextField, Box, Typography, useTheme, Switch } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useAsync } from 'react-use';

import { labelCss, boxCss } from '../../common/style';
import { Email, Link, Phone } from '@menuzen/icon/build/outline';
import { BuilderComponentProps, useNode, GeoLocation } from '@mnz/builder';
import LocationPicker from '../../common/components/location-picker/LocationPicker';
import CircularProgress from '@mui/material/CircularProgress';

export interface ContactSettingProps extends BuilderComponentProps {
    geoLocation: GeoLocation;
    hiddenContactItems: Partial<Record<HiddenContactKey, boolean>>;
}

export type HiddenContactKey = 'PHONE' | 'EMAIL' | 'WEBSITE' | 'LOCATION';

export interface LocationValues {
    phone: string;
    email: string;
    website: string;
    city: string;
    country: string;
    countryCode: string;
    latitude: number | null;
    longitude: number | null;
    postcode: string | null;
    state: string;
    address: string;
    timezone: string;
}

export const ContactSetting: FC<ContactSettingProps> = ({ geoLocation }) => {
    const theme = useTheme();

    const [defaultLocation, setDefaultLocation] = useState<{ lat: number; lng: number } | null>(null);

    const {
        actions: { setAccount, setProp },
        props: { account, hiddenContactItems },
    } = useNode((node) => {
        return {
            hidden: node.data.hidden,
            props: node.data.props as unknown as ContactSettingProps,
        };
    });

    useAsync(async () => {
        if (
            account &&
            account.latitude !== undefined &&
            account.longitude !== undefined &&
            account.latitude !== null &&
            account.longitude !== null
        ) {
            setDefaultLocation({
                lat: account.latitude,
                lng: account.longitude,
            });
        } else {
            if (geoLocation) {
                setDefaultLocation(geoLocation);
            }
        }
    }, [account, geoLocation]);

    return (
        <Box mb={2}>
            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={labelCss()}>Business Phone</Typography>
                    <Switch
                        checked={
                            hiddenContactItems && typeof hiddenContactItems.PHONE !== 'undefined'
                                ? !hiddenContactItems.PHONE
                                : true
                        }
                        onChange={(e) => {
                            setProp<ContactSettingProps>((props) => {
                                if (typeof hiddenContactItems === 'undefined') {
                                    props.hiddenContactItems = {};
                                }
                                if (typeof hiddenContactItems?.PHONE === 'undefined') {
                                    props.hiddenContactItems.PHONE = true;
                                }
                                props.hiddenContactItems.PHONE = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                <TextField
                    sx={{ marginTop: 1.5 }}
                    defaultValue={account.phone}
                    placeholder="Business Phone"
                    fullWidth={true}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ fontSize: 18 }}>
                                <Phone />
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => setAccount({ phone: e.target.value })}
                />
            </Box>

            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={labelCss()}>Business Email</Typography>
                    <Switch
                        checked={
                            hiddenContactItems && typeof hiddenContactItems.EMAIL !== 'undefined'
                                ? !hiddenContactItems.EMAIL
                                : true
                        }
                        onChange={(e) => {
                            setProp<ContactSettingProps>((props) => {
                                if (typeof hiddenContactItems === 'undefined') {
                                    props.hiddenContactItems = {};
                                }
                                if (typeof hiddenContactItems?.EMAIL === 'undefined') {
                                    props.hiddenContactItems.EMAIL = true;
                                }
                                props.hiddenContactItems.EMAIL = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                <TextField
                    sx={{ marginTop: 1.5 }}
                    defaultValue={account.email}
                    placeholder="Business Email"
                    fullWidth={true}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ fontSize: 18 }}>
                                <Email />
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => setAccount({ email: e.target.value })}
                />
            </Box>

            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={labelCss()}>Business Website</Typography>
                    <Switch
                        checked={
                            hiddenContactItems && typeof hiddenContactItems.WEBSITE !== 'undefined'
                                ? !hiddenContactItems.WEBSITE
                                : true
                        }
                        onChange={(e) => {
                            setProp<ContactSettingProps>((props) => {
                                if (typeof hiddenContactItems === 'undefined') {
                                    props.hiddenContactItems = {};
                                }
                                if (typeof hiddenContactItems?.WEBSITE === 'undefined') {
                                    props.hiddenContactItems.WEBSITE = true;
                                }
                                props.hiddenContactItems.WEBSITE = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                <TextField
                    sx={{ marginTop: 1.5 }}
                    defaultValue={account.website}
                    placeholder="Website"
                    fullWidth={true}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ fontSize: 18 }}>
                                <Link />
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => setAccount({ website: e.target.value })}
                />
            </Box>

            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={labelCss()}>Business Location</Typography>
                    <Switch
                        checked={
                            hiddenContactItems && typeof hiddenContactItems.LOCATION !== 'undefined'
                                ? !hiddenContactItems.LOCATION
                                : true
                        }
                        onChange={(e) => {
                            setProp<ContactSettingProps>((props) => {
                                if (typeof hiddenContactItems === 'undefined') {
                                    props.hiddenContactItems = {};
                                }
                                if (typeof hiddenContactItems?.LOCATION === 'undefined') {
                                    props.hiddenContactItems.LOCATION = true;
                                }
                                props.hiddenContactItems.LOCATION = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                {!defaultLocation && (
                    <Box display="flex" width="100%" justifyContent="center" my={3}>
                        <CircularProgress />
                    </Box>
                )}
                {defaultLocation && (
                    <LocationPicker
                        defaultLocation={defaultLocation}
                        place={{
                            postcode: account.postcode,
                            country: account.country,
                            countryCode: account.countryCode,
                            state: account.state,
                            city: account.city,
                            street: account.address,
                            timezone: account.timezone,
                            latitude: account.latitude,
                            longitude: account.longitude,
                        }}
                        onChange={(data: LocationValues) => {
                            setAccount(data);
                        }}
                        onChangeAddress={(address: string) => setAccount({ address })}
                        address={account.address}
                        onChangeCustomAddress={(customAddress) => setAccount({ customAddress })}
                        customAddress={account.customAddress}
                    />
                )}
            </Box>
        </Box>
    );
};
